import { createServerFn } from "@tanstack/react-start";

import {
  addCatalogItemVariantInputSchema,
  catalogItemByIdInputSchema,
  listCatalogItemsInputSchema,
  requestCatalogItemInputSchema,
  updateCatalogItemInputSchema,
  type AddCatalogItemVariantBrowserInput,
  type CatalogItemByIdBrowserInput,
  type ListCatalogItemsBrowserInput,
  type RequestCatalogItemBrowserInput,
  type UpdateCatalogItemBrowserInput,
} from "@/api/contracts/catalog";
import { requireAuth } from "@/api/middleware/auth";
import { BffUserFacingError } from "@/lib/errors";
import { derivePermissions } from "@/lib/roles";
import type { ResolvedSession } from "@/server/use-cases/auth/resolve-session";

// The browser's doors onto the item catalog: one page of the list, the category
// tree its filter sidebar and dialogs offer, one item's detail, and the three
// writes the ported screens make — request, delete, and add a variant.
//
// Three gates stand between a browser and a catalog row, and each exists
// because the others do not cover its case:
//   1. `requireAuth` — no session, no call. Throws `UnauthorizedError`, which
//      the scrub allowlist passes through so the UI can tell an expired session
//      from a server fault.
//   2. `assertCanBrowseCatalog` below — this session's roles must grant the
//      capability. It is what stops an *authenticated* identity with no catalog
//      reach (the trade partner, the system actor) from reaching the endpoint,
//      and it runs here rather than in the route guard because a server
//      function is callable without ever rendering the route. It sits in the
//      handler rather than in a middleware beside `requireAuth` so that it is
//      reachable from a plain unit test.
//   3. Python's own `require_any_role(...)`, and the use case behind it. That is
//      the enforcement (#67 §4); the two BFF gates are defense in depth and a
//      better error.
//
// **Gate 2 is per-verb, because Python's gate is.** The two reads front
// `CATALOG_READ_ROLES` and assert `canBrowseCatalog`; the create fronts
// `CATALOG_EDIT_ROLES` and asserts `canWriteCatalogItems`. Reading one capability
// across all three would either refuse the read-only user Python serves or admit
// the one it refuses.
//
// Gate 2 reads a capability rather than a role name, and pins the Python gate
// it fronts — `client/docs/architecture.md` ("Authentication & authorization")
// permits a BFF-side check on no other terms. The pins are
// `@/lib/catalog-read-access-drift.unit.test.ts` and
// `@/lib/catalog-write-access-drift.unit.test.ts`, each of which fails if its
// two sides' role sets diverge or if Python's dependency leaves the endpoint.
//
// Error scrubbing is global function middleware (`start.ts`) — no per-function
// wrapper. Server-only modules are pulled in with a dynamic `import()` inside
// the handler so the bundler keeps them out of the client bundle.

interface HandlerContext {
  session: ResolvedSession;
}

/**
 * The capability, not the role name, so a future internal role that reads the
 * catalog needs no edit here — only a row in `GRANTS`.
 *
 * The tag is the bare `role-forbidden`, which is on the user-facing allowlist
 * (`@/api/contracts/user-facing-error-tags.json`), so the message reaches the
 * browser intact instead of being scrubbed to a generic 500.
 */
export function assertCanBrowseCatalog(session: ResolvedSession): void {
  if (derivePermissions(session.roles).canBrowseCatalog) return;
  throw new BffUserFacingError(
    "role-forbidden",
    "You don't have permission to browse the item catalog.",
    403,
  );
}

/**
 * The write half of the ladder, mirroring Python's `CATALOG_EDIT_ROLES`.
 *
 * A *different* capability from the read above, and it has to be: read-only
 * signs in and browses the whole catalog but may not request an item, so a
 * create fronted by `canBrowseCatalog` would admit exactly the role Python
 * refuses. Pinned by `@/lib/catalog-write-access-drift.unit.test.ts`.
 */
function assertCanWriteCatalogItems(session: ResolvedSession): void {
  if (derivePermissions(session.roles).canWriteCatalogItems) return;
  throw new BffUserFacingError(
    "role-forbidden",
    "You don't have permission to request or change catalog items.",
    403,
  );
}

async function handleList({
  data,
  context,
}: {
  data: ListCatalogItemsBrowserInput;
  context: HandlerContext;
}) {
  assertCanBrowseCatalog(context.session);
  const { listInternalCatalogItems } = await import("@/server/data/internal-api/catalog");
  // `data` passes straight through: the declaration's encoder owns which keys
  // reach the wire, and re-deciding that here would be the same job done twice
  // in the layer that does not own the wire shape.
  return listInternalCatalogItems(data, { sessionId: context.session.sessionId });
}

/**
 * The category tree behind the filter sidebar's category select.
 *
 * The same capability gate as the list, because Python fronts the tree read
 * with the same `CATALOG_READ_ROLES` dependency and the same use-case
 * assertion — a BFF check may only ever *equal* the gate it fronts
 * (`client/docs/architecture.md`, "Authentication & authorization").
 *
 * No input at all: the read is the whole alive tree, derived from `parent_id`
 * on every request, and the select narrows it in the browser.
 */
async function handleCategoryTree({ context }: { context: HandlerContext }) {
  assertCanBrowseCatalog(context.session);
  const { getInternalCatalogCategoryTree } = await import("@/server/data/internal-api/catalog");
  return getInternalCatalogCategoryTree(undefined, { sessionId: context.session.sessionId });
}

/**
 * One requested item — the Request Item dialog's submit.
 *
 * The item lands in Requested status because the create verb has no way to say
 * otherwise: the wire face carries no Internal Use Only fields and Python's
 * `create_item` refuses a patch that names them. Nothing here has to arrange
 * that, and nothing here should try.
 */
async function handleCreate({
  data,
  context,
}: {
  data: RequestCatalogItemBrowserInput;
  context: HandlerContext;
}) {
  assertCanWriteCatalogItems(context.session);
  const { createInternalCatalogItem } = await import("@/server/data/internal-api/catalog");
  // Straight through, like `handleList`: the declaration's encoder owns which
  // keys reach the wire.
  return createInternalCatalogItem(data, { sessionId: context.session.sessionId });
}

/**
 * One item's full picture — the details page's read.
 *
 * The *read* capability, not the write one: the details page is what a
 * read-only session signs in to look at, and Python fronts `get_item` with the
 * same `CATALOG_READ_ROLES` the list and the tree carry.
 *
 * A `null` return is not an error and must not become one. The declaration is
 * `nullOn` the not-found tag, so an id that never existed — or one whose row is
 * gone — arrives here as `null` and travels onward unchanged, which is what
 * lets the route render its 404 instead of a server fault.
 */
async function handleItemDetail({
  data,
  context,
}: {
  data: CatalogItemByIdBrowserInput;
  context: HandlerContext;
}) {
  assertCanBrowseCatalog(context.session);
  const { getInternalCatalogItem } = await import("@/server/data/internal-api/catalog");
  return getInternalCatalogItem(data, { sessionId: context.session.sessionId });
}

/**
 * Retire one item — the details page's Delete, soft on the Python side.
 *
 * The *write* capability, mirroring Python's `CATALOG_EDIT_ROLES`, and it is
 * only the role half of the question. Two narrower gates sit behind it and
 * neither can live here: the per-item division narrowing needs the item to
 * evaluate against, and the refusal while any specification uses the item is
 * Python's `delete-refused:item` — a tagged, user-facing error that reaches the
 * browser intact. The menu's disabled state is the affordance for that second
 * one, never the enforcement.
 */
async function handleItemDelete({
  data,
  context,
}: {
  data: CatalogItemByIdBrowserInput;
  context: HandlerContext;
}) {
  assertCanWriteCatalogItems(context.session);
  const { deleteInternalCatalogItem } = await import("@/server/data/internal-api/catalog");
  return deleteInternalCatalogItem(data, { sessionId: context.session.sessionId });
}

/**
 * One variant of an item — the Add Variant dialog's submit.
 *
 * The *write* capability, mirroring Python's `CATALOG_EDIT_ROLES`, and the same
 * role half of the question the delete asks. Two narrower gates sit behind it
 * and neither can live here: the per-item division narrowing needs the parent to
 * evaluate against, and the refusal for a parent filed under the Unspecified
 * Manufacturer is Python's `record-locked:manufacturer`. The menu's disabled
 * entry is the affordance for the second one, never the enforcement.
 *
 * The response is the **new variant's** detail, not the parent's — which is what
 * the details page needs to switch onto it.
 *
 * Straight through, like the create: the declaration's `path()` takes `itemId`
 * and its encoder decides which of the rest reach the body. Splitting them here
 * would be the same job done twice in the layer that does not own the wire
 * shape.
 */
async function handleAddVariant({
  data,
  context,
}: {
  data: AddCatalogItemVariantBrowserInput;
  context: HandlerContext;
}) {
  assertCanWriteCatalogItems(context.session);
  const { addInternalCatalogItemVariant } = await import("@/server/data/internal-api/catalog");
  return addInternalCatalogItemVariant(data, { sessionId: context.session.sessionId });
}

/**
 * Apply the item edit page's changed fields — the Save button.
 *
 * The *write* capability, mirroring Python's `CATALOG_EDIT_ROLES`, and — as on
 * delete — only the role half of the question. Two narrower gates stay on the
 * Python side because neither can be answered here: the per-item division
 * narrowing needs the item to evaluate against, and the admin-only refusal on
 * the four Internal Use Only fields is a *field*-level rule
 * (`role-forbidden:catalog:internal-use-only`,
 * `role-forbidden:catalog:core-status`) rather than a route-level one. The page
 * renders that section for admins only, which is the affordance; Python's gate
 * is the enforcement, and this gate may only ever equal the one it fronts
 * (`client/docs/architecture.md`, "Authentication & authorization").
 *
 * `data` passes straight through, like every other handler here: the
 * declaration's encoder owns which keys reach the wire, and it names only the
 * ones the caller actually set — so the diff the form built stays a diff.
 */
async function handleItemUpdate({
  data,
  context,
}: {
  data: UpdateCatalogItemBrowserInput;
  context: HandlerContext;
}) {
  assertCanWriteCatalogItems(context.session);
  const { updateInternalCatalogItem } = await import("@/server/data/internal-api/catalog");
  return updateInternalCatalogItem(data, { sessionId: context.session.sessionId });
}

/**
 * The handlers, exported for their unit tests.
 *
 * A `createServerFn` value is not callable as a plain function — invoking it
 * goes through Start's RPC plumbing — so the gate would otherwise only be
 * reachable from an end-to-end test. Nothing on the request path imports this.
 */
export const __test_handlers = {
  handleList,
  handleCreate,
  handleCategoryTree,
  handleItemDetail,
  handleItemDelete,
  handleAddVariant,
  handleItemUpdate,
};

export const catalogItemsList = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  // `.validator`, not the deprecated `.inputValidator`. A rejection here is
  // scrubbed to the generic message like any other error (see
  // `api/middleware/safe-error.ts`) — which is right for this input: every
  // value comes from the page's own controls, so a rejection is a bug in this
  // codebase rather than something to explain to a user.
  .validator(listCatalogItemsInputSchema)
  .handler(handleList);

export const catalogItemCreate = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  // Scrubbed to the generic message on rejection, like every validator here —
  // which is right, because the dialog refuses to submit an incomplete draft and
  // says which field is missing (`@/lib/request-item-form`). A rejection at this
  // seam can only be a bug in this codebase. Python's own field-level refusals
  // arrive as tagged `invalid-input:catalog-item` errors, which the scrub
  // allowlist passes through intact.
  .validator(requestCatalogItemInputSchema)
  .handler(handleCreate);

/**
 * The category tree, on GET.
 *
 * No `.validator`: there is no input to validate. GET rather than POST because
 * nothing about this read needs a body — ADR-0008's rule is about *filters*
 * that would otherwise ride an unsigned query string, and this read has none.
 */
export const catalogCategoryTree = createServerFn({ method: "GET" })
  .middleware([requireAuth])
  .handler(handleCategoryTree);

/**
 * One item's detail, on POST.
 *
 * POST rather than GET even though the id would fit in a path: a
 * `createServerFn` GET carries its input as a query parameter, and this seam's
 * validator is what turns a typed URL's id into a number before it reaches the
 * wire. The internal call behind it is still the declaration's GET — ADR-0008
 * is about the *internal* wire, and nothing here changes it.
 */
export const catalogItemDetail = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(catalogItemByIdInputSchema)
  .handler(handleItemDetail);

export const catalogItemDelete = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(catalogItemByIdInputSchema)
  .handler(handleItemDelete);

export const catalogItemAddVariant = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(addCatalogItemVariantInputSchema)
  .handler(handleAddVariant);

/**
 * One item's changed fields, on POST.
 *
 * A rejection here is scrubbed to the generic message like every validator on
 * this seam — right, because the form refuses to submit a draft with a problem
 * outstanding and says which field has it (`@/lib/edit-item-form`), so a
 * rejection can only be a caller that is not the form. Python's own field-level
 * refusals arrive as tagged `invalid-input:catalog-item` errors, which the
 * scrub allowlist passes through intact and the page shows verbatim.
 */
export const catalogItemUpdate = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(updateCatalogItemInputSchema)
  .handler(handleItemUpdate);
